import type {
  AnyEvent,
  ArtistPersona,
  AudioState,
  AxisDelta,
  SonicTraits,
} from "../domain/index.js";
import { getApiBase, getToken } from "./auth.js";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly kind: string | undefined,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface Job {
  id: string;
  projectId: string;
  kind: string;
  status: string;
  payload: unknown;
  attempts: number;
  maxAttempts: number;
  lastError?: string | null;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const headers: Record<string, string> = { Authorization: `Bearer ${getToken()}` };
  let payload: BodyInit | undefined;
  if (body !== undefined) {
    if (body instanceof FormData) {
      payload = body;
    } else {
      headers["Content-Type"] = "application/json";
      payload = JSON.stringify(body);
    }
  }
  const init: RequestInit = { method, headers };
  if (payload !== undefined) init.body = payload;
  if (signal) init.signal = signal;
  const res = await fetch(`${getApiBase()}${path}`, init);
  if (!res.ok) {
    let kind: string | undefined;
    let message = res.statusText;
    try {
      const errBody = (await res.json()) as { kind?: string; message?: string };
      kind = errBody.kind;
      if (errBody.message) message = errBody.message;
    } catch {
      // non-JSON body
    }
    throw new ApiError(res.status, kind, message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export function getProjectState(
  projectId: string,
  signal?: AbortSignal,
): Promise<{ state: AudioState; lastGlobalSeq: number; tipHash: string }> {
  return request("GET", `/projects/${encodeURIComponent(projectId)}/state`, undefined, signal);
}

export function listEventsSince(
  projectId: string,
  sinceGlobalSeq: number,
  signal?: AbortSignal,
): Promise<{ events: AnyEvent[] }> {
  return request(
    "GET",
    `/events?projectId=${encodeURIComponent(projectId)}&since=${sinceGlobalSeq}`,
    undefined,
    signal,
  );
}

export function appendEvent(
  envelope: AnyEvent,
  signal?: AbortSignal,
): Promise<{ globalSeq: number }> {
  return request("POST", "/events", { envelope }, signal);
}

/** Job kinds a command can enqueue (mirrors `EnqueuedJobKind` in studio-api). */
export type EnqueuedJobKind = "generation" | "section-regeneration";

export function runCommand(
  projectId: string,
  text: string,
  signal?: AbortSignal,
): Promise<{
  appendedEventIds: string[];
  enqueuedJobs: { jobId: string; kind: EnqueuedJobKind }[];
}> {
  return request("POST", "/commands", { projectId, text }, signal);
}

export function undo(
  projectId: string,
  signal?: AbortSignal,
): Promise<{ appendedEventId: string; globalSeq: number }> {
  return request("POST", "/events/undo", { projectId }, signal);
}

export interface CreateJobInput {
  projectId: string;
  kind: string;
  payload: unknown;
  maxAttempts?: number;
}
export function createJob(input: CreateJobInput, signal?: AbortSignal): Promise<{ jobId: string }> {
  return request("POST", "/jobs", input, signal);
}

export function listJobs(
  filter: { projectId?: string; status?: string; kind?: string; limit?: number },
  signal?: AbortSignal,
): Promise<{ jobs: Job[] }> {
  const params = new URLSearchParams();
  if (filter.projectId) params.set("projectId", filter.projectId);
  if (filter.status) params.set("status", filter.status);
  if (filter.kind) params.set("kind", filter.kind);
  if (filter.limit !== undefined) params.set("limit", String(filter.limit));
  const qs = params.toString();
  return request("GET", `/jobs${qs ? `?${qs}` : ""}`, undefined, signal);
}

export function getJob(jobId: string, signal?: AbortSignal): Promise<{ job: Job }> {
  return request("GET", `/jobs/${encodeURIComponent(jobId)}`, undefined, signal);
}

export async function uploadAsset(
  projectId: string,
  file: File,
  signal?: AbortSignal,
): Promise<{ hash: string }> {
  const form = new FormData();
  form.append("file", file);
  return request("POST", `/assets/${encodeURIComponent(projectId)}`, form, signal);
}

/**
 * Atomic DNA-reference upload (Plan 13 §5.1). One multipart POST writes the
 * asset, appends a `ReferenceUploaded` musical event, and enqueues the
 * `dna-analysis` job — server-side, so the event and the job never diverge.
 * Replaces the old `POST /assets` + `POST /jobs` pair used by `DnaReference`.
 * `role` defaults to `"primary"` (the fresh clip becomes the active reference);
 * pass `"secondary"` for a second clip meant for a mashup.
 */
export async function uploadReference(
  projectId: string,
  file: File,
  role: "primary" | "secondary" = "primary",
  signal?: AbortSignal,
): Promise<{ referenceId: string; assetHash: string }> {
  const form = new FormData();
  form.append("file", file);
  form.append("label", file.name);
  form.append("role", role);
  return request("POST", `/songs/${encodeURIComponent(projectId)}/references`, form, signal);
}

/**
 * Removes a DNA reference from a project (spec §10 #10 / §12 un-defer). The
 * server appends a `ReferenceRemoved` event; the reducer drops the entry on
 * replay. Append-only — the original `ReferenceUploaded` stays in the log.
 * The endpoint is idempotent and returns 204 No Content; a duplicate DELETE
 * (double-click, retry) does not 404 — it is a safe no-op.
 */
export function removeReference(
  projectId: string,
  referenceId: string,
  signal?: AbortSignal,
): Promise<void> {
  return request(
    "DELETE",
    `/songs/${encodeURIComponent(projectId)}/references/${encodeURIComponent(referenceId)}`,
    undefined,
    signal,
  );
}

/**
 * Blends two DNA references of a project into a mashup (Plan 13 §8.2). A TYPED
 * call: the real `referenceId`s go straight to `POST /songs/:id/references/blend`,
 * so a blend is never routed by free-text label — two references with the same
 * name stay unambiguous (F10).
 */
export function blendReferences(
  projectId: string,
  referenceIdA: string,
  referenceIdB: string,
  label?: string,
  signal?: AbortSignal,
): Promise<{ referenceId: string; appendedEventId: string }> {
  const body: { referenceIdA: string; referenceIdB: string; label?: string } = {
    referenceIdA,
    referenceIdB,
  };
  if (label !== undefined) body.label = label;
  return request("POST", `/songs/${encodeURIComponent(projectId)}/references/blend`, body, signal);
}

export function assetUrl(projectId: string, hash: string): string {
  return `${getApiBase()}/assets/${encodeURIComponent(projectId)}/${encodeURIComponent(hash)}?token=${encodeURIComponent(getToken())}`;
}

export function eventStreamUrl(projectId: string): string {
  return `${getApiBase()}/events/stream?projectId=${encodeURIComponent(projectId)}&token=${encodeURIComponent(getToken())}`;
}

export function getCost(
  projectId: string,
  signal?: AbortSignal,
): Promise<{ spentUsd: number; capUsd: number }> {
  return request("GET", `/projects/${encodeURIComponent(projectId)}/cost`, undefined, signal);
}

// --- Artists ---

export interface LpcLayers {
  body: { id: string };
  hair: { id: string; color: string };
  shirt: { id: string; color: string };
  pants: { id: string; color: string };
  shoes?: { id: string };
  accessory?: { id: string; color?: string };
}

export interface Artist {
  id: string;
  name: string;
  lpc_layers: LpcLayers;
  /** Structured sonic identity (R3.1). `{}` for a never-edited / pre-008 artist. */
  sonic_traits: SonicTraits;
  /** Persona — bio + temperament + influences (Plan 13). `{}` for pre-012 / never-edited. */
  persona: ArtistPersona;
  created_at: string;
  deleted_at: string | null;
}

export function listArtists(signal?: AbortSignal): Promise<{ artists: Artist[] }> {
  return request("GET", "/artists", undefined, signal);
}
export function getArtist(id: string, signal?: AbortSignal): Promise<{ artist: Artist }> {
  return request("GET", `/artists/${encodeURIComponent(id)}`, undefined, signal);
}
export function createArtist(
  input: {
    name: string;
    lpc_layers: LpcLayers;
    sonic_traits?: SonicTraits;
    persona?: ArtistPersona;
  },
  signal?: AbortSignal,
): Promise<{ artist: Artist }> {
  return request("POST", "/artists", input, signal);
}
export function updateArtist(
  id: string,
  input: {
    name?: string;
    lpc_layers?: LpcLayers;
    sonic_traits?: SonicTraits;
    persona?: ArtistPersona;
  },
  signal?: AbortSignal,
): Promise<{ artist: Artist }> {
  return request("PATCH", `/artists/${encodeURIComponent(id)}`, input, signal);
}
export function deleteArtist(id: string, signal?: AbortSignal): Promise<void> {
  return request("DELETE", `/artists/${encodeURIComponent(id)}`, undefined, signal);
}
export function getArtistDiscography(
  id: string,
  signal?: AbortSignal,
): Promise<{ discography: Discography }> {
  return request("GET", `/artists/${encodeURIComponent(id)}/discography`, undefined, signal);
}

// --- Bands ---

export type SlotRole = "vocal" | "guitar" | "bass" | "drums" | "keys";
export const SLOT_ROLES: SlotRole[] = ["vocal", "guitar", "bass", "drums", "keys"];

export type StagePreset = "underground" | "jazz" | "openair" | "garage" | "synthwave" | "acoustic";
export const STAGE_PRESETS: StagePreset[] = [
  "underground",
  "jazz",
  "openair",
  "garage",
  "synthwave",
  "acoustic",
];

export type LightColor = "warm" | "cool" | "magenta" | "green" | "white" | "amber";
export const LIGHT_COLORS: LightColor[] = ["warm", "cool", "magenta", "green", "white", "amber"];

/** Per-slot trait-aggregation weights, 0..5 (R3.1). Lives inside stage_config. */
export type SlotWeights = Record<SlotRole, number>;

/** Mirrors `DEFAULT_SLOT_WEIGHTS` from `@studio/domain` — vocal/guitar carry more. */
export const DEFAULT_SLOT_WEIGHTS: SlotWeights = {
  vocal: 3,
  guitar: 3,
  bass: 1,
  drums: 1,
  keys: 2,
};

export interface StageConfig {
  preset: StagePreset;
  bannerText?: string;
  bannerColor: LightColor;
  lightLeft: LightColor;
  lightRight: LightColor;
  smoke: boolean;
  drumRiser: boolean;
  ampStack: boolean;
  /** Trait-aggregation weights per slot (R3.1). The API fills the default. */
  slotWeights: SlotWeights;
}

export const DEFAULT_STAGE: StageConfig = {
  preset: "underground",
  bannerColor: "amber",
  lightLeft: "amber",
  lightRight: "cool",
  smoke: false,
  drumRiser: false,
  ampStack: false,
  slotWeights: DEFAULT_SLOT_WEIGHTS,
};

export interface BandSlotResolved {
  slot_role: SlotRole;
  artist: Artist;
}

export interface Band {
  id: string;
  name: string;
  created_at: string;
  deleted_at: string | null;
  slots: BandSlotResolved[];
  song_count: number;
  stage_config: StageConfig;
  /** Read-only sonic traits derived from the slot artists + slotWeights (R3.1). */
  aggregated_traits: SonicTraits;
  /** Read-only persona derived from the slot artists + slotWeights (Plan 13). */
  aggregated_persona: ArtistPersona;
}

export function listBands(signal?: AbortSignal): Promise<{ bands: Band[] }> {
  return request("GET", "/bands", undefined, signal);
}
export function getBand(id: string, signal?: AbortSignal): Promise<{ band: Band }> {
  return request("GET", `/bands/${encodeURIComponent(id)}`, undefined, signal);
}
export function createBand(
  input: { name: string; slots: { slot_role: SlotRole; artist_id: string }[] },
  signal?: AbortSignal,
): Promise<{ band: Band }> {
  return request("POST", "/bands", input, signal);
}
export function renameBand(
  id: string,
  name: string,
  signal?: AbortSignal,
): Promise<{ band: Band }> {
  return request("PATCH", `/bands/${encodeURIComponent(id)}`, { name }, signal);
}
export function upsertBandSlot(
  id: string,
  role: SlotRole,
  artistId: string,
  signal?: AbortSignal,
): Promise<{ band: Band }> {
  return request(
    "PUT",
    `/bands/${encodeURIComponent(id)}/slots/${encodeURIComponent(role)}`,
    { artist_id: artistId },
    signal,
  );
}
export function removeBandSlot(
  id: string,
  role: SlotRole,
  signal?: AbortSignal,
): Promise<{ band: Band }> {
  return request(
    "DELETE",
    `/bands/${encodeURIComponent(id)}/slots/${encodeURIComponent(role)}`,
    undefined,
    signal,
  );
}
export function deleteBand(id: string, signal?: AbortSignal): Promise<void> {
  return request("DELETE", `/bands/${encodeURIComponent(id)}`, undefined, signal);
}
export function updateBandStage(
  id: string,
  config: StageConfig,
  signal?: AbortSignal,
): Promise<{ band: Band }> {
  return request("PUT", `/bands/${encodeURIComponent(id)}/stage`, config, signal);
}

export interface BandCostSummary {
  band_id: string;
  usd_this_month: number;
  cap_usd: number;
  percent: number;
}
export function getBandCostSummary(id: string, signal?: AbortSignal): Promise<BandCostSummary> {
  return request("GET", `/bands/${encodeURIComponent(id)}/cost-summary`, undefined, signal);
}

export interface BandDeletionImpact {
  band_id: string;
  song_count: number;
  bytes_on_disk: number;
  mb_on_disk: number;
}
export function getBandDeletionImpact(
  id: string,
  signal?: AbortSignal,
): Promise<BandDeletionImpact> {
  return request("GET", `/bands/${encodeURIComponent(id)}/deletion-impact`, undefined, signal);
}

// --- Chemistry A/B preview (Plan 13 §6.2) ---

/** One hypothetical slot swap: a new artist for a slot, or `null` to empty it. */
export interface ChemistrySwap {
  slot_role: SlotRole;
  artist_id: string | null;
}

/** One side of the chemistry A/B — the aggregated identity + the prompt text. */
export interface ChemistrySide {
  traits: SonicTraits;
  persona: ArtistPersona;
  /** The exact text folded into the prompt: `renderSonicTraits` + `renderPersona`. */
  promptSnippet: string;
}

/** The chemistry A/B preview result — a pure, free recomputation, no job. */
export interface ChemistryPreview {
  current: ChemistrySide;
  hypothetical: ChemistrySide;
  delta: { traits: AxisDelta; persona: AxisDelta };
}

/**
 * Recompute the band's aggregate with hypothetical slot swaps applied. A pure,
 * free preview — fires no job and no provider call, and never mutates
 * `band_slots` (spec §6, §12 #8/#9).
 */
export function bandChemistryPreview(
  id: string,
  swaps: ChemistrySwap[],
  signal?: AbortSignal,
): Promise<{ preview: ChemistryPreview }> {
  return request("POST", `/bands/${encodeURIComponent(id)}/chemistry-preview`, { swaps }, signal);
}

// --- Discography (Plan 13 §7.2) ---

/** One entry of a discography trajectory — a song with its effective traits. */
export interface DiscographyEntry {
  project_id: string;
  title: string | null;
  title_status: TitleStatus;
  created_at: string;
  has_audio: boolean;
  traits: SonicTraits;
}

/** The style-evolution summary — the first vs. last trait-bearing entry. */
export interface DiscographyEvolution {
  first: SonicTraits;
  last: SonicTraits;
  delta: AxisDelta;
}

/** A band's or artist's chronological discography — a derived view. */
export interface Discography {
  entries: DiscographyEntry[];
  /** `null` when fewer than two trait-bearing entries exist. */
  evolution: DiscographyEvolution | null;
}

/** The chronological trajectory of a band's songs (spec §7.2). */
export function getBandDiscography(
  id: string,
  signal?: AbortSignal,
): Promise<{ discography: Discography }> {
  return request("GET", `/bands/${encodeURIComponent(id)}/discography`, undefined, signal);
}

// --- Songs ---

export type TitleStatus = "pending" | "done" | "skipped" | "manual";

export interface Song {
  project_id: string;
  band_id: string | null;
  title: string | null;
  title_status: TitleStatus;
  created_at: string;
}

export function listSongs(
  filter: { bandId?: string; withAudio?: boolean },
  signal?: AbortSignal,
): Promise<{ songs: Song[] }> {
  const params = new URLSearchParams();
  if (filter.bandId) params.set("bandId", filter.bandId);
  if (filter.withAudio) params.set("withAudio", "true");
  const qs = params.toString();
  return request("GET", `/songs${qs ? `?${qs}` : ""}`, undefined, signal);
}
export function listSongsByBand(bandId: string, signal?: AbortSignal): Promise<{ songs: Song[] }> {
  return request("GET", `/bands/${encodeURIComponent(bandId)}/songs`, undefined, signal);
}
export function createSong(
  input: { bandId?: string; title?: string; projectId?: string },
  signal?: AbortSignal,
): Promise<{ song: Song }> {
  return request("POST", "/songs", input, signal);
}
export function updateSong(
  projectId: string,
  input: { bandId?: string | null; title?: string },
  signal?: AbortSignal,
): Promise<{ song: Song }> {
  return request("PATCH", `/songs/${encodeURIComponent(projectId)}`, input, signal);
}
export function getSong(
  projectId: string,
  signal?: AbortSignal,
): Promise<{ song: Song & { state: AudioState } }> {
  return request("GET", `/songs/${encodeURIComponent(projectId)}`, undefined, signal);
}

/**
 * Fans out one `generation` job per section, server-side (spec §4.5, Q3). The
 * endpoint is NOT idempotent — each call enqueues N fresh jobs — so the caller
 * must guard against double-firing (see `compositionStore.generateSong`).
 */
export function generateSong(
  projectId: string,
  signal?: AbortSignal,
): Promise<{ jobIds: string[] }> {
  return request("POST", `/songs/${encodeURIComponent(projectId)}/generate`, undefined, signal);
}

/**
 * Enqueues a single `section-regeneration` job for one section. The first-class
 * door for the Regenerate button — server-side it validates the section exists
 * against materialized state (400 if not) and the worker rebuilds the effective
 * prompt from current state, so no `prompt` is passed.
 */
export function regenerateSection(
  projectId: string,
  sectionId: string,
  signal?: AbortSignal,
): Promise<{ jobId: string }> {
  return request(
    "POST",
    `/songs/${encodeURIComponent(projectId)}/sections/${encodeURIComponent(sectionId)}/regenerate`,
    undefined,
    signal,
  );
}

/**
 * Enqueues a single `clip-render` job that produces a downloadable MP4 of the
 * song's live stage (Plan 13 §7). Server-side it validates the song has at
 * least one section with audio (400 if not). Mirrors `generateSong`.
 */
export function renderClip(projectId: string, signal?: AbortSignal): Promise<{ jobId: string }> {
  return request("POST", `/songs/${encodeURIComponent(projectId)}/clip`, undefined, signal);
}

/**
 * Fans out N `section-variation` jobs for one section — a batch of
 * alternative-take candidates (spec §5.1). Server-side it validates the section
 * exists (400 if not). NOT idempotent — each call starts a fresh batch — so the
 * caller guards against double-firing.
 */
export function generateVariations(
  projectId: string,
  sectionId: string,
  count: number,
  signal?: AbortSignal,
): Promise<{ batchId: string; jobIds: string[]; variationIds: string[] }> {
  return request(
    "POST",
    `/songs/${encodeURIComponent(projectId)}/sections/${encodeURIComponent(sectionId)}/variations`,
    { count },
    signal,
  );
}

/**
 * Promotes one of a section's variations to be its active clip (spec §5.2).
 * Server-side it reads materialized state to fill the swap, validates the
 * candidate exists (400 if not), and appends a `SectionVariationPromoted`
 * musical event.
 */
export function promoteVariation(
  projectId: string,
  sectionId: string,
  variationId: string,
  signal?: AbortSignal,
): Promise<{ appendedEventId: string; globalSeq: number }> {
  return request(
    "POST",
    `/songs/${encodeURIComponent(projectId)}/sections/${encodeURIComponent(sectionId)}/promote`,
    { variationId },
    signal,
  );
}

// --- History tree (spec §6.3) ---

/** One node of the history-tree projection — mirrors the API `HistoryNode`. */
export interface HistoryNode {
  eventId: string;
  globalSeq: number;
  type: string;
  category: "musical" | "operational";
  createdAt: string;
  createdBy: string;
  summary: string;
  /** The branch this node belongs to; `null` = the main trunk. */
  branchId: string | null;
}

/** One branch marker of the history tree — mirrors the API `HistoryBranch`. */
export interface HistoryBranch {
  branchId: string;
  fromEventId: string;
  label: string;
}

export interface HistoryProjection {
  nodes: HistoryNode[];
  branches: HistoryBranch[];
}

/**
 * Reads the history-tree projection of a project: every event as a node with a
 * human-readable `summary`, plus the branch markers (spec §6.3). Read-only.
 */
export function getHistory(projectId: string, signal?: AbortSignal): Promise<HistoryProjection> {
  return request("GET", `/songs/${encodeURIComponent(projectId)}/history`, undefined, signal);
}

/**
 * Read-only time travel: materializes the song's `AudioState` AS OF `eventId`
 * (inclusive) — spec §7.2. Nothing is appended to the log; the present state is
 * untouched. Use it to preview any point in the history tree.
 */
export function getStateAt(
  projectId: string,
  eventId: string,
  signal?: AbortSignal,
): Promise<{ state: AudioState }> {
  return request<{ song: Song & { state: AudioState } }>(
    "GET",
    `/songs/${encodeURIComponent(projectId)}?at=${encodeURIComponent(eventId)}`,
    undefined,
    signal,
  ).then(({ song }) => ({ state: song.state }));
}

/**
 * Marks a history branch off `fromEventId` (spec §7.3). The server generates
 * the `branchId`, validates `fromEventId` belongs to the project, and appends a
 * `HistoryBranchCreated` operational event — the musical `AudioState` is
 * unchanged.
 */
export function createBranch(
  projectId: string,
  fromEventId: string,
  label: string,
  signal?: AbortSignal,
): Promise<{ branchId: string; appendedEventId: string; globalSeq: number }> {
  return request(
    "POST",
    `/songs/${encodeURIComponent(projectId)}/branch`,
    { fromEventId, label },
    signal,
  );
}

// --- Library (spec §F7 + D14) ---

export interface LibrarySection {
  id: string;
  name: string;
  assetHash: string;
  startMs: number;
  endMs: number;
}

export interface LibraryEntry {
  song: Song;
  band: Band | null;
  sections: LibrarySection[];
}

export function fetchLibrary(
  filter: { bandIds?: string[] } = {},
  signal?: AbortSignal,
): Promise<{ entries: LibraryEntry[] }> {
  const params = new URLSearchParams();
  for (const id of filter.bandIds ?? []) {
    if (id) params.append("bandId", id);
  }
  const qs = params.toString();
  return request("GET", `/library${qs ? `?${qs}` : ""}`, undefined, signal);
}
